import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { WarningTwoIcon } from "@chakra-ui/icons";
import { Mail } from "lucide-react";
import type { WhitelistEntry } from "../../../../types/whitelist";

export interface SetEmailModalProps {
  entry: WhitelistEntry | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (ghostEmail: string, realEmail: string) => Promise<void>;
  isSaving: boolean;
}

export const SetEmailModal: React.FC<SetEmailModalProps> = ({ entry, isOpen, onClose, onSave, isSaving }) => {
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");

  const bg         = useColorModeValue("white", "#0f1f3a");
  const inputBg    = useColorModeValue("gray.50", "#0a1628");
  const labelColor = useColorModeValue("gray.500", "gray.400");
  const warnBg     = useColorModeValue("orange.50", "rgba(251,146,60,0.08)");
  const warnBorder = useColorModeValue("orange.200", "rgba(251,146,60,0.3)");

  useEffect(() => {
    if (isOpen) { setEmailInput(""); setEmailError(""); }
  }, [isOpen]);

  const validate = (v: string): string => {
    if (!v) return "E-mail obrigatório.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)) return "Formato de e-mail inválido.";
    if (v.endsWith("@ghostcandidate.gupy") || v.endsWith("@ghost.gg-hub"))
      return "Informe o e-mail real do candidato.";
    return "";
  };

  const handleSave = () => {
    if (!entry) return;
    const email = emailInput.trim().toLowerCase();
    const err = validate(email);
    if (err) { setEmailError(err); return; }
    onSave(entry.email, email);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
      <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.400" />
      <ModalContent borderRadius="2xl" bg={bg}>
        <ModalHeader fontSize="md" fontWeight={600} pb={1}>Informar e-mail do candidato</ModalHeader>
        <ModalCloseButton />
        <ModalBody pt={2} pb={4}>
          {entry && (
            <VStack align="stretch" spacing={4}>
              <Box p={3} borderRadius="lg" bg={warnBg} borderWidth="1px" borderColor={warnBorder}>
                <HStack spacing={2} mb={1}>
                  <WarningTwoIcon color="orange.400" boxSize="12px" />
                  <Text fontSize="xs" fontWeight={700} color="orange.500">E-mail não disponível pelo Gupy</Text>
                </HStack>
                <Text fontSize="sm" fontWeight={600} noOfLines={1}>
                  {entry.candidate_full_name ?? "Candidato sem nome"}
                </Text>
                <Text fontSize="xs" color={labelColor} mt={0.5}>
                  Este candidato chegou sem e-mail válido. Informe o e-mail real abaixo para liberar o acesso e enviar o e-mail de boas-vindas automaticamente.
                </Text>
              </Box>

              <FormControl isInvalid={!!emailError}>
                <FormLabel mb={1} fontSize="sm" fontWeight={500}>
                  E-mail real do candidato
                </FormLabel>
                <Input
                  placeholder="candidato@email.com"
                  type="email"
                  value={emailInput}
                  onChange={(e) => { setEmailInput(e.target.value); if (emailError) setEmailError(""); }}
                  onBlur={() => { if (emailInput.trim()) setEmailError(validate(emailInput.trim().toLowerCase())); }}
                  isDisabled={isSaving}
                  bg={inputBg}
                  borderRadius="lg"
                  size="sm"
                  autoFocus
                  _focus={{ borderColor: "orange.400", boxShadow: "0 0 0 1px var(--chakra-colors-orange-400)" }}
                />
                <FormErrorMessage fontSize="xs" mt={1}>{emailError}</FormErrorMessage>
              </FormControl>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter gap={2} pt={2}>
          <Button variant="ghost" size="sm" onClick={onClose} isDisabled={isSaving}>Cancelar</Button>
          <Button
            colorScheme="orange"
            size="sm"
            onClick={handleSave}
            isLoading={isSaving}
            borderRadius="lg"
            leftIcon={<Icon as={Mail} boxSize="13px" />}
          >
            Salvar e enviar e-mail
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
